import React from "react";
import HealthCard from "./HealthCard";
import { formatNumber, semicircleDash } from "../utils";

/**
 * fmd/21 data:
 * {
 *   lateCompleted,
 *   pmCompliancePercent,
 *   totalVehicles,
 *   totalPredictionsBeforeToday,
 *   completedPredictionsBeforeToday,
 *   earlyCompleted,
 *   overdueCount
 * }
 */
const unwrapPmPayload = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  if (raw.pmCompliancePercent !== undefined || raw.totalVehicles !== undefined) {
    return raw;
  }
  if (raw.data && typeof raw.data === "object") return raw.data;
  return raw;
};

const PmCompliance = ({ translate, data, loading }) => {
  const payload = unwrapPmPayload(data);
  const percent = Number(payload?.pmCompliancePercent ?? 0);
  const totalVehicles = Number(payload?.totalVehicles ?? 0);
  const due = Number(payload?.totalPredictionsBeforeToday ?? 0);
  const completed = Number(payload?.completedPredictionsBeforeToday ?? 0);
  const earlyCompleted = Number(payload?.earlyCompleted ?? 0);
  const lateCompleted = Number(payload?.lateCompleted ?? 0);
  const overdue = Number(payload?.overdueCount ?? 0);
  const { dashArray } = semicircleDash(percent, 48);

  const badge =
    percent >= 80
      ? {
          text: translate("good"),
          style: { background: "#e8f5e8", color: "#3f9c40" },
        }
      : percent >= 60
        ? {
            text: translate("fair"),
            style: { background: "#fdf3e0", color: "#b3781a" },
          }
        : {
            text: translate("poor"),
            style: { background: "#fdeaea", color: "#e0484c" },
          };

  const gaugeColor =
    percent >= 80 ? "#3f9c40" : percent >= 60 ? "#f0ad2e" : "#e0484c";

  return (
    <HealthCard
      className="fh-span-3"
      title={translate("pmCompliance")}
      subtitle={translate("pmComplianceSub")}
      badge={
        totalVehicles
          ? `${formatNumber(completed)} ${translate("total")} · ${badge.text}`
          : badge.text
      }
      badgeStyle={badge.style}
      loading={loading}
    >
      <div className="fh-kpi-visual">
        <svg width="132" height="88" viewBox="0 0 140 100">
          <path
            d="M22 84 A48 48 0 0 1 118 84"
            fill="none"
            stroke="#eef1f4"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d="M22 84 A48 48 0 0 1 118 84"
            fill="none"
            stroke={gaugeColor}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={dashArray}
          />
          <text
            x="70"
            y="72"
            textAnchor="middle"
            fontSize="20"
            fontWeight="700"
            fill="#22303c"
          >
            {formatNumber(percent, 1)}%
          </text>
          <text
            x="70"
            y="88"
            textAnchor="middle"
            fontSize="10"
            fill="#8a97a3"
          >
            {translate("onTime")}
          </text>
        </svg>
      </div>
      <div className="fh-kpi-meta fh-pm-grid">
        <div className="fh-metaline">
          <span>{translate("totalVehicles")}</span>
          <b>{formatNumber(totalVehicles)}</b>
        </div>
        <div className="fh-metaline">
          <span>{translate("pmDueToDate")}</span>
          <b>{formatNumber(due)}</b>
        </div>
        <div className="fh-metaline">
          <span>{translate("completedOnTime")}</span>
          <b>{formatNumber(completed)}</b>
        </div>
        <div className="fh-metaline">
          <span>{translate("earlyCompleted") || "Early completed"}</span>
          <b>{formatNumber(earlyCompleted)}</b>
        </div>
        <div className="fh-metaline">
          <span>{translate("lateCompleted") || "Late completed"}</span>
          <b>{formatNumber(lateCompleted)}</b>
        </div>
        <div className="fh-metaline">
          <span className="flag">{translate("overdue")}</span>
          <b className="flag">{formatNumber(overdue)}</b>
        </div>
      </div>
    </HealthCard>
  );
};

export default PmCompliance;
